<template>
  <div class="detail-field" @click="$emit('click', $event)">
    <span class="detail-label">{{ label }}</span>
    <div class="detail-value" :class="{ 'detail-value--interactive': interactive }">
      <slot />
      <transition name="fade-saved">
        <span v-if="saved" class="field-saved"><i class="tim-icons icon-check-2"></i> Saved</span>
      </transition>
    </div>
  </div>
</template>

<script>
// One label/value row inside a details TicketCard. Slot content may use these
// helper classes, which this component styles for both themes:
//   .detail-text-value   plain text value
//   .detail-icon         leading icon
//   .detail-select       borderless el-select (add .detail-select--hidden to
//                        show only the caret, e.g. behind a badge)
//   .detail-datepicker   base-input wrapping a borderless el-date-picker
//   .prop-avatar-img / .prop-avatar-initials   24px avatar
// `interactive` adds the hover highlight for editable rows; `saved` flashes
// the green Saved indicator.
export default {
  name: 'ticket-detail-field',
  props: {
    label: { type: String, required: true },
    interactive: { type: Boolean, default: false },
    saved: { type: Boolean, default: false }
  }
}
</script>

<style scoped>
.detail-field {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  min-height: 44px;
  border-radius: 4px;
  transition: background 0.15s ease;
  position: relative;
}
.detail-field:hover .detail-value--interactive {
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
}
.detail-label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
  min-width: 105px;
  flex-shrink: 0;
}
.detail-value {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 4px 8px;
  min-height: 38px;
  transition: background 0.15s ease;
}
.detail-value ::v-deep .detail-text-value {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}
.detail-value ::v-deep .detail-icon {
  font-size: 14px;
  color: white;
}
.field-saved {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: #00f2c3;
}
.field-saved i {
  font-size: 10px;
}
.fade-saved-enter-active {
  transition: opacity 0.2s ease;
}
.fade-saved-leave-active {
  transition: opacity 0.5s ease;
}
.fade-saved-enter, .fade-saved-leave-to {
  opacity: 0;
}

/* Borderless select inside the value */
.detail-value ::v-deep .detail-select {
  flex: 1;
  min-width: 0;
}
.detail-value ::v-deep .detail-select .el-input__inner {
  border: none !important;
  background: transparent !important;
  color: rgba(255, 255, 255, 0.9) !important;
  padding-left: 0 !important;
  padding-right: 18px !important;
  height: 30px !important;
  line-height: 30px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  text-align: left !important;
  text-overflow: ellipsis !important;
  overflow: hidden !important;
  white-space: nowrap !important;
}
.detail-value ::v-deep .detail-select .el-input__suffix {
  right: 0;
}
.detail-value ::v-deep .detail-select .el-select__caret {
  color: rgba(255, 255, 255, 0.15) !important;
  font-size: 12px !important;
  transition: color 0.15s ease;
}
.detail-field:hover .detail-value ::v-deep .detail-select .el-select__caret {
  color: rgba(255, 255, 255, 0.45) !important;
}
.detail-value ::v-deep .detail-select .el-input.is-focus .el-input__inner {
  border: none !important;
}
.detail-value ::v-deep .detail-select--hidden .el-input__inner {
  color: transparent !important;
  width: 0 !important;
  padding: 0 !important;
  min-width: 0 !important;
}
.detail-value ::v-deep .detail-select--hidden .el-input__suffix {
  display: none;
}

/* Borderless date picker inside the value */
.detail-value ::v-deep .detail-datepicker {
  flex: 1;
  min-width: 0;
}
.detail-value ::v-deep .detail-datepicker .el-date-editor.el-input {
  width: 100% !important;
}
.detail-value ::v-deep .detail-datepicker .el-input__inner {
  border: none !important;
  background: transparent !important;
  color: rgba(255, 255, 255, 0.9) !important;
  padding-left: 0 !important;
  padding-right: 18px !important;
  height: 30px !important;
  line-height: 30px !important;
  font-size: 13px !important;
  text-align: left !important;
}
.detail-value ::v-deep .detail-datepicker .el-input__prefix {
  display: none;
}
.detail-value ::v-deep .detail-datepicker .el-input__suffix {
  right: 0;
}
.detail-value ::v-deep .detail-datepicker .form-control {
  border: none;
  background: transparent;
  padding: 0;
}

/* Avatars */
.detail-value ::v-deep .prop-avatar-img {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
}
.detail-value ::v-deep .prop-avatar-initials {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #1d8cf8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

/* Light mode */
.white-content .detail-field:hover .detail-value--interactive {
  background: rgba(0, 0, 0, 0.04);
}
.white-content .detail-label {
  color: #525f7f;
}
.white-content .detail-value ::v-deep .detail-text-value {
  color: #344675;
}
.white-content .detail-value ::v-deep .detail-icon {
  color: #344675;
}
.white-content .detail-value ::v-deep .prop-avatar-img {
  border-color: rgba(0, 0, 0, 0.12);
}
.white-content .detail-value ::v-deep .detail-select .el-input__inner {
  color: #344675 !important;
}
.white-content .detail-value ::v-deep .detail-select .el-select__caret {
  color: rgba(0, 0, 0, 0.15) !important;
}
.white-content .detail-value ::v-deep .detail-datepicker .el-input__inner {
  color: #344675 !important;
}
</style>
